import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import Warning from "./Warning";
import { DelegeeRevokeModel, RevokeProductModel } from "../../models/vehicle-assignments/RevokePedestrianRightsModel";
import { revokePedestrianRights } from "../../services/pedestrianRights.service";
import useAppContext from "../../context/hooks/useAppContext";
import { useAppDispatch, useAppSelector } from "../../store";
import { toggleShowRevokePedestrianAction } from "../../store/actions/pedestrianRights.actions";
import {
    isPedestrianMyProductsRowKey,
    PedestrianMyProductsRowKey,
} from "../../containers/FleetManager/MyProducts/ParkingRightsDataTable/common";
import { groupBy } from "lodash";
import { useFleetManagerContext } from "../../context/hooks";
import { PedestrianRightsMyFleetState } from "../../models/delegations/DelegatedPedestrianRight";

interface IFormProps {
    selectedPedestrianIds: string[];
}

function getRevokeModelFromMyProductKeys(
    rowKeys: PedestrianMyProductsRowKey[]
): DelegeeRevokeModel[] {
    const delegeeRevokeModels = Object.entries(groupBy(rowKeys, (rowKey) => rowKey.delegeeId)).map(
        ([delegeeId, entries]): DelegeeRevokeModel => {
            return {
                delegeeId,
                products: entries.map(({entryRightId, pmcId }) => ({  entryRightId, pmcId })),
            };
        }
    );
    return delegeeRevokeModels;
}

function getEntryRightIds(delegeeId: string, myFleet: PedestrianRightsMyFleetState): RevokeProductModel[] {
    for (const pageNumber in myFleet.delegees.pages) {
        for (const delegee of myFleet.delegees.pages[pageNumber as any as number].data || []) {
            if (delegee.registrationId === delegeeId) {
                return delegee.products.map((product): RevokeProductModel  => {
                    return {
                        entryRightId: product.parkingRightId,
                        pmcId: product.pmcId,
                    };
                });
            }
        }
    }

    return [];
}

function getDelegeeRevokeModesForMyFleet(
    selectedPedestrianIds: string[],
    myFleet: PedestrianRightsMyFleetState
): DelegeeRevokeModel[] {
    return selectedPedestrianIds
        .map((delegeeId: string) => {
            return {
                delegeeId,
                products: getEntryRightIds(delegeeId, myFleet),
            };
        })
        .filter((x) => x.products.length > 0);
}

const RevokePedestrianWarning = ({ selectedPedestrianIds }: IFormProps) => {
    const { t } = useTranslation(["revokePedestrianWarning", "globals"]);
    const { appState } = useAppContext();
    const dispatch = useAppDispatch();

    const loading = useAppSelector((state) => state.parkingProducts.loading);
    const myFleet = useAppSelector((state) => state.pedestrianRights.myFleet);

    const delegeeRevokeModels = useMemo(() => {
        if (selectedPedestrianIds.length === 0) {
            return [];
        }
        if (isPedestrianMyProductsRowKey(selectedPedestrianIds[0])) {
            return getRevokeModelFromMyProductKeys(
                selectedPedestrianIds as any as PedestrianMyProductsRowKey[]
            );
        }
        return getDelegeeRevokeModesForMyFleet(selectedPedestrianIds, myFleet);
    }, [selectedPedestrianIds, myFleet]);

    const revokeCount = useMemo(
        () => delegeeRevokeModels.reduce((acc, model) => acc + model.products.length, 0),
        [delegeeRevokeModels]
    );

    const warningText = t("warning", { count: revokeCount });
    const warningDetails = t("details", { count: revokeCount });
    const toggleRevokePedestrianWarning = () => dispatch(toggleShowRevokePedestrianAction());
    const { setSelectedParkingRightIds } = useFleetManagerContext();

    const handleRevokeCancellation = () => {
        toggleRevokePedestrianWarning();
    };

    const handleSuccessfulRevoke = () => {
        toggleRevokePedestrianWarning();
        setSelectedParkingRightIds([]);
    };

    const handleRevokeConfirmation = () => {
        if (selectedPedestrianIds.length === 0) {
            throw new Error("shouldn't happen");
        }

        return dispatch(
            revokePedestrianRights(
                {
                    customerId: appState.user.seasonTicketOwnerCrmId!,
                    delegeeRevokeModels,
                },
                handleSuccessfulRevoke
            )
        );
    };

    return (
        <Warning
            warning={warningText}
            details={warningDetails}
            onCancel={handleRevokeCancellation}
            onConfirm={handleRevokeConfirmation}
            disabled={false}
            confirmText={t("globals:revoke")}
            loading={loading}
        />
    );
};

export default RevokePedestrianWarning;
